/**
 * Firebase accessors. The legacy app loads firebase-compat from CDN in index.html
 * and initializes it in www/app.js. This module wraps that already-initialized
 * instance and exposes a clean API.
 *
 * In a later phase we'll move the CDN scripts + init into here.
 */

import { createLogger } from './logger.js';

const log = createLogger('firebase');

/** @type {any} */
let _firebase = null;
/** @type {Promise<void> | null} */
let _authReadyPromise = null;

/**
 * Wait until the legacy app.js has set `window.firebase` and called initializeApp.
 * @returns {Promise<any>}
 */
function waitForFirebase() {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const tick = () => {
      const fb = /** @type {any} */ (window).firebase;
      if (fb && fb.apps && fb.apps.length > 0) {
        resolve(fb);
        return;
      }
      if (Date.now() - start > 10_000) {
        reject(new Error('Firebase SDK not ready after 10s'));
        return;
      }
      setTimeout(tick, 50);
    };
    tick();
  });
}

/**
 * Get the firebase namespace. Throws if not yet initialized (use init() first).
 * @returns {any}
 */
export function getFirebase() {
  if (!_firebase) {
    const fb = /** @type {any} */ (window).firebase;
    if (!fb || !fb.apps || fb.apps.length === 0) {
      throw new Error('Firebase not initialized. Call init() first.');
    }
    _firebase = fb;
  }
  return _firebase;
}

/** @returns {any} */
export function auth() { return getFirebase().auth(); }

/** @returns {any} */
export function db() { return getFirebase().database(); }

/**
 * Get a database ref at the given path.
 * @param {string} path
 * @returns {any}
 */
export function ref(path) { return db().ref(path); }

/**
 * Wait for the legacy SDK to be loaded and resolve. Safe to call multiple times.
 * @returns {Promise<void>}
 */
export async function init() {
  if (_firebase) return;
  _firebase = await waitForFirebase();
  log.info('linked to legacy-initialized Firebase SDK');
}

/**
 * Resolve once a user (anonymous or signed-in) is present. Mirrors the legacy
 * `ensureAuthReady()` so existing flows can migrate gradually.
 * @returns {Promise<void>}
 */
export function ensureAuthReady() {
  if (_authReadyPromise) return _authReadyPromise;
  _authReadyPromise = (async () => {
    await init();
    const a = auth();
    if (a.currentUser) return;
    await new Promise((resolve) => {
      const unsub = a.onAuthStateChanged((/** @type {any} */ user) => {
        if (user) {
          unsub();
          resolve(undefined);
        } else {
          a.signInAnonymously().catch((/** @type {Error} */ err) => {
            log.error('anonymous sign-in failed:', err);
          });
        }
      });
    });
  })();
  return _authReadyPromise;
}

/**
 * Read a Firebase path once, with timeout.
 * @template T
 * @param {string} path
 * @param {number} [timeoutMs=8000]
 * @returns {Promise<T | null>}
 */
export async function readOnce(path, timeoutMs = 8000) {
  const snap = await withTimeout(ref(path).once('value'), timeoutMs, `read ${path}`);
  return /** @type {T | null} */ (snap.val() ?? null);
}

/**
 * Wrap a promise with a timeout that rejects.
 * @template T
 * @param {Promise<T>} promise
 * @param {number} ms
 * @param {string} label
 * @returns {Promise<T>}
 */
export function withTimeout(promise, ms, label = 'op') {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Timeout (${ms}ms): ${label}`)), ms);
    promise.then(
      (v) => { clearTimeout(timer); resolve(v); },
      (err) => { clearTimeout(timer); reject(err); }
    );
  });
}
